// src/components/TimeCapsuleCard.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Avatar,
    CircularProgress,
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';  // fixed path

/**
 * Reusable card component for displaying a time capsule preview.
 *
 * Props:
 * - capsule: the time capsule data object
 * - containerRef: a React ref to the wrapper Box (used for measuring height)
 * - onLoad: callback called with the container height once the image loads
 */
const TimeCapsuleCard = ({ capsule, containerRef, onLoad }) => {
    const { senderId, capsuleType, media, messageTitle, messageBody, location, sentAt } = capsule;
    const isRestricted = senderId && (capsuleType === 'private' || capsuleType === 'memberExclusive');

    const [senderInfo, setSenderInfo] = useState(null);
    const [loadingSender, setLoadingSender] = useState(false);

    // only run when senderId or capsuleType changes
    useEffect(() => {
        const fetchSenderInfo = async () => {
            if (!isRestricted) return;
            setLoadingSender(true);
            try {
                const userSnap = await getDoc(doc(db, 'users', senderId));
                if (userSnap.exists()) {
                    const data = userSnap.data();
                    setSenderInfo({
                        displayName: data.displayName,
                        profileImage: data.profileImage,
                    });
                }
            } catch (err) {
                console.error('Failed to fetch sender info:', err);
            }
            setLoadingSender(false);
        };
        fetchSenderInfo();
    }, [senderId, capsuleType, isRestricted]);

    // loading state for restricted capsules
    if (isRestricted && loadingSender) {
        return (
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // format sentAt timestamp
    const sentAtDate = sentAt?.seconds
        ? new Date(sentAt.seconds * 1000)
        : null;
    const formattedDate = sentAtDate
        ? sentAtDate.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        })
        : '';

    return (
        <Box ref={containerRef}>
            <Card square elevation={0} sx={{ boxShadow: 'none', borderRadius: 0 }}>
                {/* Image */}
                {media?.[0]?.url && (
                    <CardMedia
                        component="img"
                        image={media[0].url}
                        alt={messageTitle}
                        sx={{
                            aspectRatio: '21/9',
                            objectFit: 'cover',
                            width: '100%',
                            borderRadius: 0,
                        }}
                        onLoad={() => {
                            if (onLoad && containerRef.current) {
                                onLoad(containerRef.current.getBoundingClientRect().height);
                            }
                        }}
                    />
                )}

                <CardContent>
                    {/* Only show sender + date for private/memberExclusive */}
                    {isRestricted && senderInfo && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                pb: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    src={senderInfo.profileImage}
                                    alt={senderInfo.displayName}
                                    sx={{ width: 32, height: 32, mr: 1 }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {senderInfo.displayName}
                                </Typography>
                            </Box>
                            {formattedDate && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ wordBreak: 'break-word' }}
                                >
                                    <strong>Sent:</strong> {formattedDate}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Title */}
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ wordBreak: 'break-word', fontSize: '1rem' }}
                    >
                        {messageTitle || messageBody}
                    </Typography>

                    {/* Body */}
                    <Typography
                        variant="body1"
                        sx={{ fontSize: '0.85rem', mb: 1, wordBreak: 'break-word' }}
                    >
                        {messageBody}
                    </Typography>

                    {/* Address */}
                    {location?.address && (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ wordBreak: 'break-word' }}
                        >
                            <strong>From:</strong> {location.address}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default TimeCapsuleCard;
